import React, { Fragment, Suspense, useEffect, useState } from 'react';
import {
  SavePlanningAndTargetConfigPayload,
  SavePlanningAndTargetConfigInput,
  ProjectConfig,
  FacilityUnitRate,
  UnitRate,
  UnitOfMeasure,
} from '@bechtel/types';
import {
  Box,
  ButtonAppearance,
  FormControl,
  Radio,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldSize,
  ToastAction,
  ToastType,
  useToastsDispatch,
  theme,
} from '@bechtel/gds';
import { GET_PROJECT_CONFIG_PLAN_TARGET_CALC, SAVE_PROJECT_CONFIG_PLAN_TARGET_CALC } from '@bechtel/client-gql'; //TODO: Will remove in data ticket//
import { NetworkStatus, useQuery, useMutation } from '@apollo/client';
import { ErrorMessage, Field, Formik } from 'formik';
import { get } from 'lodash';
import { number, object } from 'yup';
import {
  ErrorBoundary,
  ErrorRetry,
  SuspenseLoader,
  useAppUser,
  ProjectAppTypes,
  useEnvProvider,
} from '@bechtel/client-common';
import { SavePlanningAndTargetConfigErrorExt } from './error/types';
import { PlanningTargetCalcProgress } from './progress';
import { FacilityList } from './facility';
import {
  FooterButtonStyled,
  Label,
  PlanningTargetCalcContainerStyled,
  PlanningTargetCalcFooterButtonStyled,
  PlanningTargetCalcSubContainerStyled,
  TextInputStyled,
} from './styled';
import PlanningTargetCalcRequestError from './error';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const FieldError = ({ msg }) => {
  return (
    <Box pt={{ sm: '400' }} pb={{ sm: '500' }}>
      <Text color="bechtel.red">{msg}</Text>
    </Box>
  );
};

export interface FormikState {
  indirectTaxPercentage: ProjectConfig['indirectTax'];
  projectMultiplier: ProjectConfig['projectAdjustmentFactor'];
  isCompositeUnitRateProjectWide: boolean;
  projectWideCompositeUnitRate: UnitRate;
  facilitiesUnitRate: FacilityUnitRate[];
}

const planningTargetCalcValidationSchema = object().shape({
  indirectTaxPercentage: number()
    .required(t`Indirect Tax is mandatory`)
    .positive(t`Indirect Tax should be greater than 0`)
    .max(100, t`Indirect Tax cannot be greater than 100`)
    .test('maxDigitsAfterDecimal', t`Indirect Tax must have 1 decimal digit`, (number) =>
      /^\d+(\.\d{0,1})?$/.test(String(number))
    ),
  projectMultiplier: number()
    .required(t`Project Multiplier is mandatory`)
    .positive(t`Project Multiplier should be greater than 0`)
    .max(100, t`Project Multiplier cannot be greater than 100`)
    .test('maxDigitsAfterDecimal', t`Project Multiplier must have 1 decimal digit`, (number) =>
      /^\d+(\.\d{0,1})?$/.test(String(number))
    ),
  projectWideCompositeUnitRate: object().when('isCompositeUnitRateProjectWide', {
    is: true,
    then: object({
      unitRate: number()
        .required(t`Project Wide Composite Unit Rate is mandatory`)
        .positive(t`Project Wide Composite Unit Rate should be greater than 0`)
        .max(100, t`Project Wide Composite Unit Rate cannot be greater than 100`)
        .test('maxDigitsAfterDecimal', t`Project Wide Composite Unit Rate must have 1 decimal digit`, (number) =>
          /^\d+(\.\d{0,1})?$/.test(String(number))
        ),
    }),
  }),
});

const PlanningTargetCalcUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Planning &amp; Target Calculations data saved successfully.</Trans>
      </Text>
    </Box>
  );
};

let formikInitialValues: FormikState = {
  indirectTaxPercentage: 0,
  projectMultiplier: 0,
  isCompositeUnitRateProjectWide: true,
  projectWideCompositeUnitRate: {
    unitRate: 0,
    //@ts-ignore avoid unnecesary assignment of mandatory props to generate declaration file
    uom: { id: '', uomTypeId: UnitOfMeasure.HOURS_PER_FOOT },
  },
  facilitiesUnitRate: [],
};

export const PlanningTargetCalc = () => {
  const { envConfig } = useEnvProvider();
  const { user } = useAppUser();
  const { i18n } = useLingui();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const dispatchToast = useToastsDispatch();
  const [savePlanningAndTargetConfigError, setSavePlanningAndTargetConfigError] =
    useState<SavePlanningAndTargetConfigErrorExt | null>();
  const { loading, error, data, refetch, networkStatus } = useQuery<{
    projectConfig: ProjectConfig;
  }>(GET_PROJECT_CONFIG_PLAN_TARGET_CALC, {
    variables: { id: user?.project?.id },
    notifyOnNetworkStatusChange: true,
  });
  const projectConfigData: ProjectConfig = get(data, 'projectConfigById', undefined);
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  useEffect(() => {
    if (!projectConfigData) return;
    formikInitialValues = {
      ...formikInitialValues,
      indirectTaxPercentage: projectConfigData.indirectTax * 100,
      projectMultiplier: projectConfigData.projectAdjustmentFactor * 100,
      projectWideCompositeUnitRate: projectConfigData.compositeUnitRate,
      facilitiesUnitRate: projectConfigData.facilityUnitRates ? projectConfigData.facilityUnitRates : [],
    };
    setInitialValues(formikInitialValues);
  }, [projectConfigData]);

  const [saveProjectPlanningTargetCalcs, { loading: updatePlanningTargetCalcsLoading }] = useMutation<
    { savePlanningTargetCalcs: SavePlanningAndTargetConfigPayload },
    { input: SavePlanningAndTargetConfigInput }
  >(SAVE_PROJECT_CONFIG_PLAN_TARGET_CALC, {
    onCompleted: (data) => {
      const planningAndTargetConfigError = get(data, 'savePlanningAndTargetConfig.error');
      if (planningAndTargetConfigError) {
        setSavePlanningAndTargetConfigError(planningAndTargetConfigError);
      } else {
        setSavePlanningAndTargetConfigError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: PlanningTargetCalcUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setSavePlanningAndTargetConfigError(error);
    },
  });

  const closeModal = () => {
    setSavePlanningAndTargetConfigError(null);
  };
  return (
    <Fragment>
      <PlanningTargetCalcContainerStyled>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="error-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>
                There was a problem loading Planning &amp; Target Calculation data. Please click the button below to try
                again.
              </Trans>
            </ErrorRetry>
          </div>
        )}

        {initialValues && !loading && !error && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={planningTargetCalcValidationSchema}
            onSubmit={async (values) => {
              const modifiedFacilities = values.facilitiesUnitRate.filter((facilityItem) => {
                return initialValues.facilitiesUnitRate.some((defaultItem) => {
                  return (
                    facilityItem.facility.id === defaultItem.facility.id &&
                    facilityItem.unitRate.unitRate !== defaultItem.unitRate.unitRate
                  );
                });
              });
              const input: SavePlanningAndTargetConfigInput = {
                indirectTax: values.indirectTaxPercentage / 100,
                projectAdjustmentFactor: values.projectMultiplier / 100,
                compositeUnitRate: {
                  unitRate: values.projectWideCompositeUnitRate.unitRate,
                  uomId: values.projectWideCompositeUnitRate.uom.id,
                },
                facilityUnitRates: modifiedFacilities.map(({ facility, unitRate }) => ({
                  facilityId: facility.id,
                  unitRate: { unitRate: unitRate.unitRate, uomId: unitRate.uom.id },
                })),
              };
              await saveProjectPlanningTargetCalcs({
                variables: { input },
              });
            }}
          >
            {(form) => {
              const { dirty, isValid, setFieldValue, values } = form;
              return (
                <>
                  <PlanningTargetCalcSubContainerStyled data-testid="target-calc-container">
                    <Box
                      flexDirection={{ sm: 'column' }}
                      flexBasis={{ sm: '60%' }}
                      css={`
                        overflow-y: auto;
                        border-right: 1px solid ${({ theme }) => theme.colors.grey[300]};
                      `}
                    >
                      <Box px={{ sm: '500' }} pt={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                        <Box mb={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
                          <Box mb={{ sm: '450' }}>
                            <Text family="graphik" size="500" color="grey.1000" weight="600">
                              <Trans>Indirect Tax Percentage</Trans>
                            </Text>
                          </Box>

                          <Text
                            family="graphik"
                            size="300"
                            color="grey.1000"
                            style={{ lineHeight: `${theme.spacings[550]}` }}
                          >
                            <Trans>
                              Set a percentage to calculate target hours. This accounts for indirect support services
                              used during work package execution (operators, etc.).
                            </Trans>
                          </Text>
                        </Box>

                        <Box flexDirection={{ sm: 'column' }} my={{ sm: '500' }} flexGrow={{ sm: '1' }}>
                          <FormControl
                            label={t`Indirect Tax (Required)`}
                            css={`
                              flex-grow: 1;
                            `}
                          >
                            <Field name="indirectTaxPercentage">
                              {({ field }) => {
                                return (
                                  <TextInputStyled
                                    data-testid="indirect-tax"
                                    type="number"
                                    size={TextFieldSize.LARGE}
                                    {...field}
                                    placeholder={t`Indirect Tax`}
                                  />
                                );
                              }}
                            </Field>
                          </FormControl>
                          <ErrorMessage name="indirectTaxPercentage">
                            {(msg) => {
                              return <FieldError msg={i18n._(msg)} />;
                            }}
                          </ErrorMessage>
                        </Box>
                        <Box
                          mt={{ sm: '450' }}
                          mb={{ sm: '550' }}
                          css={`
                            border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                          `}
                        />
                        <Box mb={{ sm: '450' }} flexDirection={{ sm: 'column' }}>
                          <Box mb={{ sm: '400' }}>
                            <Text family="graphik" size="500" color="grey.1000" weight="600">
                              <Trans>Project Multiplier</Trans>
                            </Text>
                          </Box>

                          <Text
                            family="graphik"
                            size="300"
                            color="grey.1000"
                            style={{ lineHeight: `${theme.spacings[550]}` }}
                          >
                            <Trans>Adjust the target calculations up or down depending on project’s needs.</Trans>
                          </Text>
                        </Box>

                        <Box flexDirection={{ sm: 'column' }} my={{ sm: '500' }} flexGrow={{ sm: '1' }}>
                          <FormControl
                            label={t`Project Multiplier (Required)`}
                            css={`
                              flex-grow: 1;
                            `}
                          >
                            <Field name="projectMultiplier">
                              {({ field }) => {
                                return (
                                  <TextInputStyled
                                    data-testid="project-multiplier"
                                    type="number"
                                    size={TextFieldSize.LARGE}
                                    {...field}
                                    placeholder={t`Project Multiplier`}
                                  />
                                );
                              }}
                            </Field>
                          </FormControl>
                          <ErrorMessage name="projectMultiplier">
                            {(msg) => {
                              return <FieldError msg={i18n._(msg)} />;
                            }}
                          </ErrorMessage>
                        </Box>

                        <Box
                          mt={{ sm: '450' }}
                          mb={{ sm: '550' }}
                          css={`
                            border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                          `}
                        />

                        <Box mb={{ sm: '300' }} flexDirection={{ sm: 'column' }}>
                          <Box mb={{ sm: '450' }}>
                            <Text family="graphik" size="500" color="grey.1000" weight="600">
                              <Trans>Composite Unit Rate</Trans>
                            </Text>
                          </Box>

                          <Text
                            family="graphik"
                            size="300"
                            color="grey.1000"
                            style={{ lineHeight: `${theme.spacings[550]}` }}
                          >
                            {t`This is the unit rate ${
                              isPFAppType ? 'PipeFighter' : 'ArcStryker'
                            } will use to estimate quantity targets in linear feet.`}
                          </Text>
                        </Box>

                        <Box justifyContent={{ sm: 'space-between' }} p={{ sm: '500' }} pb={{ sm: '400' }}>
                          <Box alignItems={{ sm: 'center' }}>
                            <Label
                              onClick={() => {
                                setFieldValue('isCompositeUnitRateProjectWide', true);
                              }}
                            >
                              <Box mr={{ sm: '500' }}>
                                <Radio readOnly checked={values.isCompositeUnitRateProjectWide} />
                              </Box>
                              <Text size="300" color="grey.1000">
                                <Trans>Set project-wide</Trans>
                              </Text>
                            </Label>
                          </Box>
                          <Box alignItems={{ sm: 'center' }}>
                            <Label
                              onClick={() => {
                                setFieldValue('isCompositeUnitRateProjectWide', false); //TODO: This functionality is not included in this ticket
                              }}
                            >
                              <Box mr={{ sm: '500' }}>
                                <Radio disabled={true} readOnly checked={!values.isCompositeUnitRateProjectWide} />
                              </Box>
                              <Text size="300" color="grey.1000">
                                <Trans>Set by individual area</Trans>
                              </Text>
                            </Label>
                          </Box>
                        </Box>

                        {values.isCompositeUnitRateProjectWide && (
                          <Box flexDirection={{ sm: 'column' }} my={{ sm: '500' }} flexGrow={{ sm: '1' }}>
                            <FormControl
                              label={t`Project-Wide (hours/foot)`}
                              css={`
                                flex-grow: 1;
                              `}
                            >
                              <Field name="projectWideCompositeUnitRate.unitRate">
                                {({ field }) => {
                                  return (
                                    <TextInputStyled
                                      data-testid="project-composite-unit-rate"
                                      type="number"
                                      size={TextFieldSize.LARGE}
                                      {...field}
                                      placeholder={t`Project-Wide`}
                                    />
                                  );
                                }}
                              </Field>
                            </FormControl>
                            <ErrorMessage name="projectWideCompositeUnitRate.unitRate">
                              {(msg) => {
                                return <FieldError msg={i18n._(msg)} />;
                              }}
                            </ErrorMessage>
                          </Box>
                        )}
                        {!values.isCompositeUnitRateProjectWide && <FacilityList />}
                      </Box>
                    </Box>
                    <Box
                      p={{ sm: '550' }}
                      pt={{ sm: '600' }}
                      flexDirection={{ sm: 'column' }}
                      flexBasis={{ sm: '40%' }}
                    >
                      <Text family="graphik" size="300" color="grey.1000">
                        <Trans>Preview:</Trans>
                      </Text>
                      <Box my={{ sm: '550' }}>
                        <Text
                          family="graphik"
                          size="200"
                          color="grey.1000"
                          style={{ lineHeight: `${theme.spacings[550]}` }}
                        >
                          <Trans>
                            This example shows the target calculation for a Superintendent with 100 craft employees
                            working 50 hours a week.
                          </Trans>
                        </Text>
                      </Box>

                      <PlanningTargetCalcProgress
                        indirectTax={values.indirectTaxPercentage}
                        projectAdjustmentFactor={values.projectMultiplier}
                        compositeUnitRate={values.projectWideCompositeUnitRate.unitRate}
                      />
                    </Box>
                  </PlanningTargetCalcSubContainerStyled>
                  {!loading && !error && (
                    <FooterButtonStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <PlanningTargetCalcFooterButtonStyled
                            data-testid="cancel-btn"
                            disabled={updatePlanningTargetCalcsLoading || !dirty}
                            onClick={() => {
                              form.handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </PlanningTargetCalcFooterButtonStyled>
                        </Box>
                        <PlanningTargetCalcFooterButtonStyled
                          data-testid="save-btn"
                          onClick={() => {
                            form.handleSubmit();
                          }}
                          disabled={updatePlanningTargetCalcsLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {updatePlanningTargetCalcsLoading && <Spinner />}
                          {!updatePlanningTargetCalcsLoading && t`Save Changes`}
                        </PlanningTargetCalcFooterButtonStyled>
                      </Box>
                    </FooterButtonStyled>
                  )}
                </>
              );
            }}
          </Formik>
        )}
      </PlanningTargetCalcContainerStyled>
      {savePlanningAndTargetConfigError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <PlanningTargetCalcRequestError
              savePlanningAndTargetConfigError={savePlanningAndTargetConfigError}
              close={closeModal}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
